import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Progress,
  Row,
} from "reactstrap";
import moment from "moment";
import Flatpickr from "react-flatpickr";
import { Vietnamese } from "flatpickr/dist/l10n/vn.js";
import AsyncSelect from "react-select/async";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import BreadCrumb from "Components/Common/BreadCrumb";
import DataTableShell, { getSttNumber } from "Components/Common/DataTableShell";
import JobErrorLogList from "Components/Common/JobErrorLogList";
import PaginationBar from "Components/Common/PaginationBar";
import NumberInput from "Components/Common/NumberInput";
import SearchByPrizeModal from "./modals/SearchByPrizeModal";
import PlanStatusColumn from "./components/PlanStatusColumn";
import ErrorPlanManagementTab from "./components/ErrorPlanManagementTab";
import HistoryTimelineModal from "./modals/HistoryTimelineModal";
import ReceiveWarehouseStepModal from "./modals/ReceiveWarehouseStepModal";
import ReasonPromptModal from "Components/Common/ReasonPromptModal";
import { PageActionPermission } from "services/pageActionPermission/PageActionPermission";
import {
  getStoredActivePrintExportJobs,
  getStoredActiveQrImageExportJobs,
  clearStoredActivePrintExportJob,
  clearStoredActiveQrImageExportJob,
} from "helpers/activeExportJobsStorage";
import {
  approveIssuancePlan,
  cancelIssuancePlan,
  createIssuancePlan,
  getIssuancePlans,
  getIssuancePlanStatuses,
  getTicketDesignTemplates,
  getActiveTicketJobsApi,
  createPrintPackageExportJob,
  createQrImageExportJob,
  getPrintPackageJobErrorLogsApi,
  getQrJobErrorLogsApi,
  receiveWarehouseIssuancePlan,
  createPrintHouseDeliveryApi,
  verifyTicketGenerationApi,
  resumeTicketCreationIssuancePlan,
  cancelQrGenerationJobApi,
  cancelPrintPackageExportJobApi,
  cancelQrImageExportJobApi,
} from "helpers/fakebackend_helper";
import {
  TICKET_DESIGN_TEMPLATE_STATUS,
  TicketDesignTemplate,
} from "pages/TicketDesignTemplate/types";
import { removeActiveJob } from "slices/sinhBoSoQr/reducer";
import { QR_GENERATION_JOB_TERMINAL_EVENT } from "hooks/useCancelQrGenerationJob";
import { toJobStatus } from "pages/SinhBoSoQr/types";
import { loadActiveTicketTypesWithCache } from "helpers/activeTicketTypesCache";
import { loadSystemConfigWithCache } from "helpers/systemConfigCache";
import { TicketType } from "pages/TicketTypeManagement/types";
import { useJobProgress } from "Components/Hooks/useJobProgress";
import {
  CreateIssuancePlanPayload,
  DEFAULT_BADGE_COLORS,
  DEFAULT_STATUS_LABELS,
  ISSUANCE_PLAN_STATUS,
  IssuancePlan,
  IssuancePlanFilters,
  IssuancePlanPagination,
  IssuancePlanStatusConfig,
  TICKET_TYPE_OPTIONS,
} from "./types";
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('issuance_plan');

type ConfirmAction = {
  title: string;
  message: string;
  confirmText: string;
  color: string;
  iconClass: string;
  action: () => Promise<any>;
  successMessage: string;
  handlesOwnFeedback?: boolean;
  // Chay sau khi action + toast/refresh thanh cong (VD dieu huong sang man khac) — xem "Duyet".
  onSuccess?: () => void;
};

type PrintPackageExportJob = {
  jobId: string;
  issuancePlanId: number;
  lotteryPeriodId?: number;
  status:
    | "pending"
    | "processing"
    | "completed"
    | "failed"
    | "stalled"
    | string;
  totalRows: number;
  processedRows: number;
  progressPercent: number;
  fileName?: string | null;
  errorMessage?: string | null;
};

type QrGenerationJob = {
  jobId: string;
  lotteryPeriodId: number;
  status: "pending" | "processing" | "completed" | "failed" | string;
  totalTickets: number;
  processedTickets: number;
  progressPercent: number;
  errorMessage?: string | null;
};

type TicketGenerationVerifyResult = {
  lotteryPeriodId: number;
  planCode: string;
  expectedDrawDate: string;
  expectedTotalTickets: number;
  generatedTotalTickets: number;
  brokenTickets: number;
  duplicateTickets: number;
  successfulTickets: number;
  missingTickets: number;
  successPercent: number;
  isValid: boolean;
};

const initialFilters: IssuancePlanFilters = {
  status: "All",
  month: "",
};

const initialPagination: IssuancePlanPagination = {
  pageIndex: 1,
  pageSize: 10,
  totalItems: 0,
  totalPages: 0,
};

const initialCreateForm: CreateIssuancePlanPayload = {
  ticketType: "Nam",
  ticketDesignTemplateId: 0,
  expectedDrawDate: "",
  expectedPrintQuantity: 100000,
  numberRangeStart: "00000",
  numberRangeEnd: "99999",
  ticketSymbols: ["A"],
};

const getPayloadItems = (payload: any) =>
  payload?.items || payload?.data?.items || payload?.data || payload || [];

const getStatusLabel = (
  status: number | string,
  configs: IssuancePlanStatusConfig[],
) =>
  configs.find((item) => Number(item.statusCode) === Number(status))
    ?.statusName ||
  DEFAULT_STATUS_LABELS[String(status)] ||
  String(status);

const getStatusColor = (
  status: number | string,
  configs: IssuancePlanStatusConfig[],
) =>
  configs.find((item) => Number(item.statusCode) === Number(status))
    ?.badgeColor ||
  DEFAULT_BADGE_COLORS[String(status)] ||
  "secondary";

const getSoftBadgeClass = (color: string) =>
  `badge bg-${color}-subtle text-${color} text-uppercase`;

const formatDate = (date?: string | null) =>
  date ? moment(date).format("DD/MM/YYYY") : "-";

const formatNumber = (value?: number | null) =>
  Number(value || 0).toLocaleString("vi-VN");
const SYNC_PRINT_PACKAGE_ROW_LIMIT = 1_000_000;
// Key theo tung jobId (khong con dung 1 key co dinh) — cho phep nhieu ky export song song deu duoc
// GlobalJobProgress khoi phuc lai sau khi F5, thay vi key cu bi ghi de khi bam "Gui in" ky thu 2.
const ACTIVE_PRINT_EXPORT_JOB_KEY_PREFIX = "lms_active_print_export_job_";
const PRINT_EXPORT_JOB_STARTED_EVENT = "lms_print_export_job_started";
// Phat ra tu GlobalJobProgress khi job export chuyen sang failed/stalled — lang nghe o day de load
// lai danh sach ngay, tranh nut "Gửi in" bi ket o trang thai disabled (canSendToPrint tinh tu luc
// job con dang chay) neu nguoi dung dang dung san o trang nay luc job that bai.
const PRINT_EXPORT_JOB_TERMINAL_EVENT = "lms_print_export_job_terminal";
// Tuong tu, danh cho luong xuat file anh QR song song (xem GlobalJobProgress.tsx).
const ACTIVE_QR_IMAGE_EXPORT_JOB_KEY_PREFIX = "lms_active_qr_image_export_job_";
const QR_IMAGE_EXPORT_JOB_STARTED_EVENT = "lms_qr_image_export_job_started";
// QR_GENERATION_JOB_TERMINAL_EVENT gio import tu hooks/useCancelQrGenerationJob.ts (dung chung voi
// nut Huy trong popup tien trinh noi va cot Thao tac cua man Sinh Bo So & QR) thay vi tu khai bao
// rieng o day — lang nghe de load lai danh sach, tranh dong ke hoach bi ket lai voi trang thai/badge
// cu du plan.Status thuc te da duoc backend chuyen sang Cancel khi huy.

const setStoredActiveQrImageExportJob = (
  jobId: string,
  issuancePlanId?: number,
) => {
  localStorage.setItem(
    `${ACTIVE_QR_IMAGE_EXPORT_JOB_KEY_PREFIX}${jobId}`,
    JSON.stringify({ jobId, issuancePlanId }),
  );
};

const setStoredActivePrintExportJob = (
  jobId: string,
  issuancePlanId?: number,
) => {
  localStorage.setItem(
    `${ACTIVE_PRINT_EXPORT_JOB_KEY_PREFIX}${jobId}`,
    JSON.stringify({ jobId, issuancePlanId }),
  );
};

const getTicketDigitsFromOptions = (
  ticketType: string,
  options: Array<{ value: string; digits: number }>,
) => options.find((item) => item.value === ticketType)?.digits || 5;

// Dai so cho moi ky hieu phu thuoc so chu so cua tung loai ve (VD: ve 5 so -> 100.000 so/ky
// hieu, ve 6 so -> 1.000.000 so/ky hieu) — KHONG duoc hard-code 1.000.000 cho moi loai ve, neu
// khong se tinh thieu ky hieu can sinh (VD: ve 5 so, 10 trieu ve se can 100 ky hieu chu khong
// phai 10) va sinh ra loi "khong du dai so/ky hieu" khi bam Sinh Bo So & QR.
const getSymbolCountByQuantity = (
  quantity?: number | null,
  digits: number = 5,
) => Math.max(1, Math.ceil(Number(quantity || 0) / Math.pow(10, digits)));

const getAlphabetSymbol = (index: number): string => {
  let value = index + 1;
  let label = "";
  while (value > 0) {
    value--;
    label = String.fromCharCode(65 + (value % 26)) + label;
    value = Math.floor(value / 26);
  }
  return label;
};

const generateTicketSymbols = (quantity?: number | null, digits: number = 5) =>
  Array.from(
    { length: getSymbolCountByQuantity(quantity, digits) },
    (_, index) => getAlphabetSymbol(index),
  );

const buildNumberRangeForQuantity = (
  quantity: number,
  symbolCount: number,
  digits: number,
) => {
  const safeQuantity = Math.max(1, Number(quantity || 1));
  const safeSymbolCount = Math.max(1, symbolCount || 1);
  const maxPerSymbol = Math.pow(10, digits);
  const perSymbol = Math.min(
    maxPerSymbol,
    Math.ceil(safeQuantity / safeSymbolCount),
  );
  return {
    numberRangeStart: "0".repeat(digits),
    numberRangeEnd: String(perSymbol - 1).padStart(digits, "0"),
  };
};

const formatDrawMonthSearchInput = (value: string) => {
  const digits = value.replace(/\D/g, "").slice(0, 6);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}/${digits.slice(2)}`;
};

const isValidDrawMonthSearch = (value: string) => {
  const match = /^(\d{2})\/(\d{4})$/.exec(value);
  if (!match) return false;
  const month = Number(match[1]);
  return month >= 1 && month <= 12;
};

const decodeJwtPayload = (token?: string) => {
  try {
    if (!token) return null;
    const payload = token.split(".")[1];
    if (!payload) return null;
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(
      decodeURIComponent(
        atob(base64)
          .split("")
          .map((char) => `%${`00${char.charCodeAt(0).toString(16)}`.slice(-2)}`)
          .join(""),
      ),
    );
  } catch {
    return null;
  }
};

const getCurrentUserRoles = (): string[] => {
  try {
    const authUser = localStorage.getItem("authUser");
    if (!authUser) return [];
    const parsed = JSON.parse(authUser);
    const tokenPayload = decodeJwtPayload(
      parsed?.token || parsed?.access_token,
    );
    const rawRoles = [
      parsed?.role,
      parsed?.roleName,
      parsed?.data?.role,
      ...(Array.isArray(parsed?.roles) ? parsed.roles : []),
      ...(Array.isArray(parsed?.roleNames) ? parsed.roleNames : []),
      tokenPayload?.role,
      tokenPayload?.Role,
      tokenPayload?.[
        "http://schemas.microsoft.com/ws/2008/06/identity/claims/role"
      ],
    ].flat();

    return rawRoles
      .filter(Boolean)
      .map((role: any) => String(role).trim().toUpperCase());
  } catch {
    return [];
  }
};

const isLeadershipUser = () => getCurrentUserRoles().includes("LEADERSHIP");

const IssuancePlanPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { activeTicketTypes } = useSelector((state: any) => state.TicketType);
  const { config: systemConfig } = useSelector(
    (state: any) => state.SystemConfiguration,
  );
  // Danh sach job sinh QR dang active toan cuc (Redux, duoc GlobalJobProgress dong bo — bao gom
  // ca job do tai khoan khac tao, xem GlobalJobProgress.tsx) — doi chieu theo lotteryPeriodId de
  // biet ke hoach nao dang co job sinh QR chay, dung cho nut "Huy" o cot Thao tac (muc 9.2).
  const { activeJobs: activeQrGenJobs } = useSelector(
    (state: any) => state.SinhBoSoQr,
  );
  const activeQrGenJobIdByPlan = useMemo(() => {
    const map = new Map<number, string>();
    ((activeQrGenJobs as QrGenerationJob[] | undefined) || []).forEach(
      (job) => {
        const status = String(job.status || "").toLowerCase();
        if (
          (status === "pending" || status === "processing") &&
          job.lotteryPeriodId
        ) {
          map.set(job.lotteryPeriodId, job.jobId);
        }
      },
    );
    return map;
  }, [activeQrGenJobs]);
  const [plans, setPlans] = useState<IssuancePlan[]>([]);
  const [statuses, setStatuses] = useState<IssuancePlanStatusConfig[]>([]);
  const [templates, setTemplates] = useState<TicketDesignTemplate[]>([]);
  // Option dang chon cho autocomplete mau ve (react-select can object {value,label}, khong chi ID)
  const [selectedTemplateOption, setSelectedTemplateOption] = useState<{
    value: number;
    label: string;
  } | null>(null);
  const loadTemplateOptionsTimer = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  // 5 mau thiet ke gan nhat, hien ngay khi bam mo dropdown luc chua go gi (AsyncSelect chi tu goi
  // loadOptions('') 1 lan duy nhat luc mount qua defaultOptions=true, khong tu lam moi moi lan mo
  // lai) — truyen mang nay vao defaultOptions de kiem soat va lam moi thu cong qua onMenuOpen.
  const [recentTemplateOptions, setRecentTemplateOptions] = useState<
    { value: number; label: string }[]
  >([]);
  const [templateSearchInput, setTemplateSearchInput] = useState("");
  const [filters, setFilters] = useState<IssuancePlanFilters>(initialFilters);
  const [monthInput, setMonthInput] = useState(initialFilters.month);
  const [pagination, setPagination] =
    useState<IssuancePlanPagination>(initialPagination);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [rejectPlan, setRejectPlan] = useState<IssuancePlan | null>(null);
  // Job (sinh QR / xuat goi in / xuat anh QR) dang duoc chon de huy tu cot "Thao tac" — mo
  // ReasonPromptModal bat nhap ly do truoc khi goi API huy tuong ung (xem REQUIRE_FIXBUGS.md muc 9.2).
  const [cancelJobTarget, setCancelJobTarget] = useState<{
    type: "qr" | "print" | "qrImage";
    jobId: string;
    planCode: string;
  } | null>(null);
  const [warehousePlan, setWarehousePlan] = useState<IssuancePlan | null>(null);
  // Ke hoach dang mo modal ghi nhan giao vé cho nha in (README.md muc 7) — chi hien khi status =
  // SentPrint, xem cung nhom nut voi "Tiep nhan" ben duoi.
  const [printHouseDeliveryPlan, setPrintHouseDeliveryPlan] =
    useState<IssuancePlan | null>(null);
  const [printHouseDeliveryForm, setPrintHouseDeliveryForm] = useState({
    printHouseName: "",
    address: "",
    password: "",
  });
  // Ke hoach dang mo modal lich su (roadmap) — xem HistoryTimelineModal.
  const [historyPlan, setHistoryPlan] = useState<IssuancePlan | null>(null);
  // Mục 13.5, REQUIRE_FIXBUGS.md — kế hoạch đang xem chi tiết lỗi (badge "Có lỗi" ở bảng danh sách).
  const [errorPlan, setErrorPlan] = useState<IssuancePlan | null>(null);
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(
    null,
  );
  const [exportJob] = useState<PrintPackageExportJob | null>(null);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportDownloading] = useState(false);
  const [qrJob, setQrJob] = useState<QrGenerationJob | null>(null);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [qrPanelCollapsed, setQrPanelCollapsed] = useState(false);
  const [exportPanelCollapsed, setExportPanelCollapsed] = useState(false);
  const [verifyModalOpen, setVerifyModalOpen] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verifyResult, setVerifyResult] =
    useState<TicketGenerationVerifyResult | null>(null);
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const [searchPlan, setSearchPlan] = useState<IssuancePlan | null>(null);
  // Popup xem đầy đủ "Dải số / ký hiệu" khi cột trong bảng bị rút gọn (text-truncate)
  const [rangeInfoPlan, setRangeInfoPlan] = useState<IssuancePlan | null>(null);
  const [createForm, setCreateForm] =
    useState<CreateIssuancePlanPayload>(initialCreateForm);
  const [ticketSymbolsText, setTicketSymbolsText] = useState("A");
  // Message hiển thị ngay dưới ô "Ký hiệu vé" khi rỗng (mục UAT-CAT-013) — trước đây Input có
  // `required` nên trình duyệt chặn submit bằng tooltip HTML5 tiếng Anh ("please fill out this
  // field") trước khi kịp chạy validate JS ở handleCreate, không hiển thị được message tiếng Việt.
  const [ticketSymbolsError, setTicketSymbolsError] = useState<string | null>(
    null,
  );
  const [actualReceivedQuantity, setActualReceivedQuantity] = useState("");
  const verifiedQrJobRef = useRef<string | null>(null);
  // Ke hoach dang co job "Gui in"/"Xuat anh QR" chay nen (planId -> jobId) — dung de disable + hien
  // spinner tren dung nut cua dong do (giong hanh vi nut "Sinh bo so & QR" o man SinhBoSoQr), tranh
  // bam lien tuc tao ra nhieu job trung nhau cho cung 1 ke hoach; jobId con dung de goi API huy tu
  // cot "Thao tac" (xem REQUIRE_FIXBUGS.md muc 9.2).
  const [printingPlanIds, setPrintingPlanIds] = useState<Map<number, string>>(
    new Map(),
  );
  const [exportingQrImagePlanIds, setExportingQrImagePlanIds] = useState<
    Map<number, string>
  >(new Map());
  const statusOptions = useMemo(
    () => [
      { statusCode: "All", statusName: st('tat_ca_text') },
      ...statuses.filter(
        (status) => Number(status.statusCode) !== ISSUANCE_PLAN_STATUS.Approved,
      ),
    ],
    [statuses],
  );

  // Danh sách loại vé đang mở (tải từ module Quản lý Loại Vé) — dùng TICKET_TYPE_OPTIONS
  // hardcode làm fallback nếu API lỗi hoặc chưa có dữ liệu, tránh block người dùng.
  const ticketTypeOptions = useMemo(
    () =>
      activeTicketTypes.length > 0
        ? activeTicketTypes.map((item: TicketType) => ({
            value: item.code,
            label: item.name,
            digits: item.digits,
          }))
        : TICKET_TYPE_OPTIONS,
    [activeTicketTypes],
  );

  // Loại vé đang được cấu hình ở System Configuration (theo ticketTypeId) — dùng để tự động
  // chọn sẵn khi mở form lập kế hoạch, thay vì bắt người dùng phải tự chọn mỗi lần.
  const configuredTicketTypeCode = useMemo(() => {
    if (!systemConfig?.ticketTypeId) return null;
    const matched = activeTicketTypes.find(
      (item: TicketType) => item.id === systemConfig.ticketTypeId,
    );
    return matched?.code ?? null;
  }, [systemConfig, activeTicketTypes]);

  const loadStatuses = useCallback(async () => {
    try {
      const response = await getIssuancePlanStatuses();
      setStatuses(getPayloadItems(response));
    } catch (error: any) {
      toast.error(
        error?.message || st('khong_the_tai_cau_hinh_trang_message'),
      );
    }
  }, []);

  const loadTemplates = useCallback(async () => {
    try {
      const response = await getTicketDesignTemplates({
        pageIndex: 1,
        pageSize: 100,
        status: TICKET_DESIGN_TEMPLATE_STATUS.Approved,
      });
      const items = getPayloadItems(response).filter(
        (item: TicketDesignTemplate) =>
          item.status === TICKET_DESIGN_TEMPLATE_STATUS.Approved,
      );
      setTemplates(items);
      return items as TicketDesignTemplate[];
    } catch (error: any) {
      toast.error(error?.message || st('khong_the_tai_danh_sach_mau_message'));
      return [] as TicketDesignTemplate[];
    }
  }, []);

  // Autocomplete mau ve: goi API tim theo tu khoa (backend da ho tro Search + gioi han pageSize=5),
  // debounce 350ms de tranh goi API tren tung ky tu go.
  const loadTemplateOptions = useCallback(
    (inputValue: string): Promise<{ value: number; label: string }[]> => {
      return new Promise((resolve) => {
        if (loadTemplateOptionsTimer.current)
          clearTimeout(loadTemplateOptionsTimer.current);
        loadTemplateOptionsTimer.current = setTimeout(async () => {
          try {
            const response = await getTicketDesignTemplates({
              pageIndex: 1,
              pageSize: 5,
              status: TICKET_DESIGN_TEMPLATE_STATUS.Approved,
              search: inputValue || undefined,
            });
            const items = getPayloadItems(response) as TicketDesignTemplate[];
            resolve(
              items.map((item) => ({
                value: Number(item.id),
                label: `${item.templateCode} - ${item.templateName}`,
              })),
            );
          } catch (error: any) {
            toast.error(error?.message || st('khong_the_tim_mau_ve_message'));
            resolve([]);
          }
        }, 350);
      });
    },
    [],
  );

  // Lay 5 mau thiet ke gan nhat (sap xep theo ngay tao moi nhat) de hien san khi nguoi dung bam
  // mo dropdown nhung chua go gi — goi lai moi lan mo (khong chi 1 lan luc mount) de luon la ban
  // ghi moi nhat, VD sau khi vua duyet them 1 mau thiet ke o tab khac.
  const loadRecentTemplateOptions = useCallback(async () => {
    try {
      const response = await getTicketDesignTemplates({
        pageIndex: 1,
        pageSize: 5,
        status: TICKET_DESIGN_TEMPLATE_STATUS.Approved,
        sortBy: "createdAt",
        sortOrder: "desc",
      });
      const items = getPayloadItems(response) as TicketDesignTemplate[];
      setRecentTemplateOptions(
        items.map((item) => ({
          value: Number(item.id),
          label: `${item.templateCode} - ${item.templateName}`,
        })),
      );
    } catch {
      // Khong show toast o day — day chi la goi ngam de goi y, khong phai thao tac nguoi dung chu dong
    }
  }, []);

  const handleTemplateMenuOpen = useCallback(() => {
    if (!templateSearchInput) loadRecentTemplateOptions();
  }, [templateSearchInput, loadRecentTemplateOptions]);

  // Kiem soat inputValue cua AsyncSelect de biet chinh xac luc nao dang "chua nhap gi" (dung o
  // handleTemplateMenuOpen o tren) — chi cap nhat khi nguoi dung thuc su go (action "input-change"),
  // bo qua cac action khac (input-blur, menu-close, set-value...) de tranh xoa nham text dang go.
  const handleTemplateInputChange = useCallback(
    (value: string, actionMeta: { action: string }) => {
      if (actionMeta.action === "input-change") {
        setTemplateSearchInput(value);
      }
      return value;
    },
    [],
  );

  const loadPlans = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {
        pageIndex: pagination.pageIndex,
        pageSize: pagination.pageSize,
      };
      if (filters.status !== "All") params.status = Number(filters.status);
      if (filters.month) params.month = filters.month;

      const response = await getIssuancePlans(params);
      const payload = response?.data || response;
      setPlans(payload.items || payload.data || payload || []);
      setPagination((prev) => ({
        ...prev,
        totalItems: payload.totalItems ?? payload.totalCount ?? 0,
        totalPages: payload.totalPages ?? 0,
      }));
    } catch (error: any) {
      toast.error(
        error?.message || st('khong_the_tai_danh_sach_ke_message'),
      );
    } finally {
      setLoading(false);
    }
  }, [
    filters.month,
    filters.status,
    pagination.pageIndex,
    pagination.pageSize,
  ]);

  useEffect(() => {
    document.title = st('ke_hoach_phat_hanh_text');
    loadStatuses();
    loadActiveTicketTypesWithCache(dispatch);
    loadSystemConfigWithCache(dispatch);
    loadRecentTemplateOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadStatuses]);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  useEffect(() => {
    const refreshAt = (location.state as { refreshAt?: number } | null)
      ?.refreshAt;
    if (refreshAt) {
      loadPlans();
    }
  }, [loadPlans, location.state]);

  useEffect(() => {
    const handleExportJobTerminal = () => loadPlans();
    window.addEventListener(
      PRINT_EXPORT_JOB_TERMINAL_EVENT,
      handleExportJobTerminal,
    );
    window.addEventListener(
      QR_GENERATION_JOB_TERMINAL_EVENT,
      handleExportJobTerminal,
    );
    return () => {
      window.removeEventListener(
        PRINT_EXPORT_JOB_TERMINAL_EVENT,
        handleExportJobTerminal,
      );
      window.removeEventListener(
        QR_GENERATION_JOB_TERMINAL_EVENT,
        handleExportJobTerminal,
      );
    };
  }, [loadPlans]);

  // Dong bo danh sach ke hoach dang co job "Gui in"/"Xuat anh QR" chay nen (doc tu localStorage,
  // dung nguon voi PlanStatusColumn) de disable + hien spinner tren nut tuong ung — lam moi luc
  // mount, moi khi co job moi bat dau/that bai (qua custom event), va poll dinh ky 5s de bat kip
  // luc job hoan tat thanh cong (khong co event rieng cho truong hop nay).
  useEffect(() => {
    const syncJobPlanIds = () => {
      setPrintingPlanIds(
        new Map(
          getStoredActivePrintExportJobs()
            .filter((job) => !!job.issuancePlanId)
            .map((job) => [job.issuancePlanId as number, job.jobId]),
        ),
      );
      setExportingQrImagePlanIds(
        new Map(
          getStoredActiveQrImageExportJobs()
            .filter((job) => !!job.issuancePlanId)
            .map((job) => [job.issuancePlanId as number, job.jobId]),
        ),
      );
    };

    syncJobPlanIds();
    const pollTimer = window.setInterval(syncJobPlanIds, 5000);
    window.addEventListener(PRINT_EXPORT_JOB_STARTED_EVENT, syncJobPlanIds);
    window.addEventListener(PRINT_EXPORT_JOB_TERMINAL_EVENT, syncJobPlanIds);
    window.addEventListener(QR_IMAGE_EXPORT_JOB_STARTED_EVENT, syncJobPlanIds);

    return () => {
      window.clearInterval(pollTimer);
      window.removeEventListener(
        PRINT_EXPORT_JOB_STARTED_EVENT,
        syncJobPlanIds,
      );
      window.removeEventListener(
        PRINT_EXPORT_JOB_TERMINAL_EVENT,
        syncJobPlanIds,
      );
      window.removeEventListener(
        QR_IMAGE_EXPORT_JOB_STARTED_EVENT,
        syncJobPlanIds,
      );
    };
  }, []);

  useEffect(() => {
    if (!isLeadershipUser()) return;

    let disposed = false;
    getActiveTicketJobsApi()
      .then((response: any) => {
        if (disposed) return;
        const payload = response?.data || response || {};
        const activeQrJob =
          payload.qrGenerationJobs?.[0] || payload.QrGenerationJobs?.[0];

        if (activeQrJob) {
          setQrJob({
            ...activeQrJob,
            status: toJobStatus(activeQrJob.status ?? activeQrJob.Status),
          });
          setQrModalOpen(true);
        }
      })
      .catch(() => undefined);

    return () => {
      disposed = true;
    };
  }, []);

  const resetCreateForm = (templatesOverride?: TicketDesignTemplate[]) => {
    // Dung danh sach mau vua tai (neu co) thay vi doc tu closure `templates` — trong closure cua
    // async onClick, state `templates` chua kip cap nhat ngay sau await loadTemplates() trong
    // cung 1 lan goi ham (stale closure), khien lan mo modal dau tien luon rong mau thiet ke.
    const templateList = templatesOverride ?? templates;
    const firstTemplate = templateList[0];
    const firstTemplateId = firstTemplate?.id ? Number(firstTemplate.id) : 0;
    setSelectedTemplateOption(
      firstTemplate?.id
        ? {
            value: Number(firstTemplate.id),
            label: `${firstTemplate.templateCode} - ${firstTemplate.templateName}`,
          }
        : null,
    );
    setTemplateSearchInput("");
    // Ưu tiên loại vé đang cấu hình ở System Configuration, nếu chưa có thì dùng mặc định cứng
    const ticketType = configuredTicketTypeCode || initialCreateForm.ticketType;
    const digits = getTicketDigitsFromOptions(ticketType, ticketTypeOptions);
    // Số lượng vé in dự kiến auto-load từ "Số lượng vé tạo mỗi kỳ" đã cấu hình ở System
    // Configuration (theo loại vé đang chọn) — nếu chưa cấu hình thì dùng mặc định cứng.
    const expectedPrintQuantity =
      systemConfig?.quantityPerPeriod ||
      initialCreateForm.expectedPrintQuantity;
    const symbols = generateTicketSymbols(expectedPrintQuantity, digits);
    const range = buildNumberRangeForQuantity(
      expectedPrintQuantity,
      symbols.length,
      digits,
    );
    setCreateForm({
      ...initialCreateForm,
      ticketType,
      expectedPrintQuantity,
      ...range,
      ticketDesignTemplateId: firstTemplateId,
    });
    setTicketSymbolsText(symbols.join(", "));
    setTicketSymbolsError(null);
  };

  const handleCreate = async (event: React.FormEvent) => {
    event.preventDefault();
    const symbols = ticketSymbolsText
      .split(",")
      .map((item) => item.trim().toUpperCase())
      .filter(Boolean);

    if (symbols.length === 0) {
      setTicketSymbolsError(
        st('vui_long_them_it_nhat_01_text'),
      );
      return;
    }
    // Chặn ký tự đặc biệt/mã lệnh (VD "<script>alert('x')</script>") — mục UAT-CAT-014. Backend
    // cũng validate lại (CreateIssuancePlanCommand) để chống bypass gọi thẳng API.
    if (symbols.some((symbol) => !/^[A-Z0-9]+$/.test(symbol))) {
      setTicketSymbolsError(
        st('ky_hieu_ve_chi_duoc_chua_text'),
      );
      return;
    }
    setTicketSymbolsError(null);

    if (!createForm.expectedDrawDate) {
      toast.error(st('vui_long_chon_ngay_quay_du_message'));
      return;
    }

    // AsyncSelect (autocomplete mau ve) khong phai the select goc nen khong con HTML5 "required" —
    // phai tu validate lai o day de giu dung hanh vi cu.
    if (!createForm.ticketDesignTemplateId) {
      toast.error(st('vui_long_chon_mau_thiet_ke_message'));
      return;
    }

    const digits = getTicketDigitsFromOptions(
      createForm.ticketType,
      ticketTypeOptions,
    );
    // Moi ky hieu chi chua toi da 10^digits so (VD: ve 5 chu so -> 100.000 so/ky hieu) — thieu ky
    // hieu se lam qua trinh sinh bo so & QR that bai sau nay (khong du dai so/ky hieu cho tong so
    // luong ve du kien), nen phai chan ngay tu buoc tao ke hoach thay vi de loi o sinh QR.
    const requiredSymbolCount = getSymbolCountByQuantity(
      createForm.expectedPrintQuantity,
      digits,
    );
    if (symbols.length < requiredSymbolCount) {
      toast.error(
        st('loai_ve_digits_chu_so_toi_message', { digits: digits, value: formatNumber(Math.pow(10, digits)) }) +
          st('value_ve_can_toi_thieu_required_message', { value: formatNumber(createForm.expectedPrintQuantity), required_symbol_count: requiredSymbolCount }) +
          st('hien_chi_co_length_message', { length: symbols.length }),
      );
      return;
    }

    const range = buildNumberRangeForQuantity(
      createForm.expectedPrintQuantity,
      symbols.length,
      digits,
    );

    setSubmitting(true);
    try {
      await createIssuancePlan({
        ...createForm,
        ...range,
        ticketSymbols: symbols,
      });
      toast.success(st('lap_ke_hoach_phat_hanh_thanh_message'));
      setCreateOpen(false);
      resetCreateForm();
      loadPlans();
    } catch (error: any) {
      toast.error(error?.message || st('khong_the_tao_ke_hoach_phat_message'));
    } finally {
      setSubmitting(false);
    }
  };

  const runAction = async (
    action: () => Promise<any>,
    successMessage: string,
    options?: { silent?: boolean },
  ) => {
    setSubmitting(true);
    try {
      await action();
      if (!options?.silent) toast.success(successMessage);
      loadPlans();
    } catch (error: any) {
      toast.error(error?.message || st('khong_the_thuc_hien_thao_tac_message'));
    } finally {
      setSubmitting(false);
    }
  };

  const openConfirmAction = (config: ConfirmAction) => {
    setConfirmAction(config);
  };

  const handleConfirmAction = async () => {
    if (!confirmAction) return;
    setSubmitting(true);
    try {
      await confirmAction.action();
      if (!confirmAction.handlesOwnFeedback) {
        toast.success(confirmAction.successMessage);
        loadPlans();
      }
      confirmAction.onSuccess?.();
      setConfirmAction(null);
    } catch (error: any) {
      toast.error(error?.message || st('khong_the_thuc_hien_thao_tac_message'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async (reason: string) => {
    if (!rejectPlan) return;
    await runAction(
      () => cancelIssuancePlan(rejectPlan.id, reason),
      st('da_huy_ke_hoach_phat_hanh_text'),
    );
    setRejectPlan(null);
  };

  // Huy job dang chay (sinh QR / xuat goi in / xuat anh QR) tu nut "Huy" o cot Thao tac — goi dung
  // API tuong ung theo loai job, kem ly do bat buoc nhap qua ReasonPromptModal (muc 9.2).
  const handleCancelActiveJob = async (reason: string) => {
    if (!cancelJobTarget) return;
    const { type, jobId } = cancelJobTarget;

    // Huy job dang chay khong can thong bao gi ca - chi can dong popup progress va quay ve
    // danh sach ke hoach (README.md, muc Cap nhat).
    await runAction(
      () => {
        if (type === "qr") return cancelQrGenerationJobApi(jobId, reason);
        if (type === "print")
          return cancelPrintPackageExportJobApi(jobId, reason);
        return cancelQrImageExportJobApi(jobId, reason);
      },
      "",
      { silent: true },
    );

    if (type === "qr") {
      // Dong ngay popup progress dang theo doi job nay - khong doi vong poll 5s tiep theo moi
      // cap nhat trang thai "cancelled" (giong cach GlobalJobProgress.tsx tu huy job cua no).
      dispatch(removeActiveJob(jobId));
      // Chi dong popup local NEU no dang theo doi dung job vua huy - popup nay co the dang theo
      // doi job cua 1 ke hoach KHAC dang chay song song (gan tu luc load trang, xem effect mount
      // ben tren), dong vo dieu kien se lam mat tien trinh hop le cua job khac.
      if (qrJob?.jobId === jobId) {
        setQrModalOpen(false);
      }
      // Xoa luon key localStorage dang danh dau job nay la "active" - neu khong xoa, lan sau
      // GlobalJobProgress remount (F5, mo tab moi) se doc lai key nay va "hoi sinh" popup cho 1
      // job nguoi dung da huy roi (xem restoreActiveJobs trong GlobalJobProgress.tsx).
      const cancelledLotteryPeriodId = (
        activeQrGenJobs as QrGenerationJob[] | undefined
      )?.find((j) => j.jobId === jobId)?.lotteryPeriodId;
      if (cancelledLotteryPeriodId) {
        localStorage.removeItem(`lms_active_job_${cancelledLotteryPeriodId}`);
      }
      // Bao cho PlanStatusColumn cua dong nay biet ngay - no co state qrGenJobId rieng doc tu
      // localStorage luc mount, khong tu re-render chi vi key localStorage vua bi xoa o tren.
      window.dispatchEvent(new CustomEvent(QR_GENERATION_JOB_TERMINAL_EVENT));
    }
    if (type === "print") clearStoredActivePrintExportJob(jobId);
    if (type === "qrImage") clearStoredActiveQrImageExportJob(jobId);
    setCancelJobTarget(null);
  };

  const handleReceiveWarehouse = async (quantity: number) => {
    if (!warehousePlan) return;
    await runAction(
      () => receiveWarehouseIssuancePlan(warehousePlan.id, quantity),
      st('da_tiep_nhan_ve_va_nhap_text'),
    );
    setWarehousePlan(null);
  };

  const handleCreatePrintHouseDelivery = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!printHouseDeliveryPlan) return;
    const { printHouseName, address, password } = printHouseDeliveryForm;
    if (!printHouseName.trim() || !address.trim() || !password.trim()) {
      toast.error(st('vui_long_nhap_day_du_ten_message'));
      return;
    }

    setSubmitting(true);
    try {
      await createPrintHouseDeliveryApi({
        issuancePlanId: printHouseDeliveryPlan.id,
        printHouseName: printHouseName.trim(),
        address: address.trim(),
        password: password.trim(),
      });
      toast.success(st('da_ghi_nhan_giao_ve_cho_message'));
      setPrintHouseDeliveryPlan(null);
    } catch (error: any) {
      toast.error(error?.message || st('khong_the_ghi_nhan_giao_ve_message'));
    } finally {
      setSubmitting(false);
    }
  };

  /*
  const downloadExportJobFile = useCallback(async (job: PrintPackageExportJob) => {
    if (!job.jobId || exportDownloading) return;

    setExportDownloading(true);
    try {
      const blob = await downloadPrintPackageExportJobWithRetry(job.jobId);
      downloadBlob(blob, job.fileName || `print-package-${job.jobId}.zip`);
      await sendToPrintIssuancePlan(job.issuancePlanId);
      toast.info('Đang tạo gói in ở nền. Trạng thái gửi nhà in sẽ cập nhật sau khi export thành công.');
      clearStoredActivePrintExportJob();
      setExportModalOpen(false);
      setExportJob(null);
      loadPlans();
    } catch (error: any) {
      toast.error(error?.message || 'Không thể tải gói in hoặc gửi nhà in');
    } finally {
      setExportDownloading(false);
    }
  }, [exportDownloading, loadPlans]);

  const isExportJobTerminal = exportJob?.status === 'completed' || exportJob?.status === 'failed' || exportJob?.status === 'stalled';
  const trackedExportJobId = exportModalOpen && !isExportJobTerminal ? (exportJob?.jobId || null) : null;

  const handleExportJobProgress = useCallback((message: {
    jobId: string;
    status: string;
    processedRows: number;
    totalRows: number;
    progressPercent: number;
    fileName?: string | null;
    errorMessage?: string | null;
  }) => {
    if (message.status === 'completed' || message.status === 'failed') {
      clearStoredActivePrintExportJob();
    }
    setExportJob(prev => (prev ? { ...prev, ...message } : prev));
  }, []);

  // Temporary polling fallback while the backend SignalR hub remains enabled.
  usePrintPackageJobProgress(trackedExportJobId, handleExportJobProgress);

  useEffect(() => {
    if (exportJob?.status !== 'completed') return;
    if (downloadedExportJobRef.current === exportJob.jobId) return;

    downloadedExportJobRef.current = exportJob.jobId;
    downloadExportJobFile(exportJob);
  }, [downloadExportJobFile, exportJob]);
  */

  const runTicketGenerationVerify = useCallback(
    async (job: QrGenerationJob) => {
      setVerifyModalOpen(true);
      setVerifyLoading(true);
      setVerifyResult(null);

      try {
        const response = await verifyTicketGenerationApi(job.lotteryPeriodId);
        const payload = response?.data || response;
        setVerifyResult(payload);
        toast.success(st('da_verify_lai_ve_so_cua_message'));
        loadPlans();
      } catch (error: any) {
        toast.error(
          error?.message || st('khong_the_verify_lai_ve_so_message'),
        );
      } finally {
        setVerifyLoading(false);
      }
    },
    [loadPlans],
  );

  const trackedQrJobId =
    qrModalOpen &&
    String(qrJob?.status || "").toLowerCase() !== "completed" &&
    String(qrJob?.status || "").toLowerCase() !== "failed" &&
    String(qrJob?.status || "").toLowerCase() !== "stalled"
      ? qrJob?.jobId || null
      : null;

  const handleQrJobProgress = useCallback(
    (message: {
      jobId: string;
      status: string;
      processedTickets: number;
      totalTickets: number;
      progressPercent: number;
      errorMessage?: string | null;
    }) => {
      const status = String(message.status || "").toLowerCase();
      const nextJob = qrJob ? { ...qrJob, ...message } : null;
      setQrJob((prev) => (prev ? { ...prev, ...message } : prev));

      if (
        status === "completed" &&
        nextJob &&
        verifiedQrJobRef.current !== message.jobId
      ) {
        verifiedQrJobRef.current = message.jobId;
        localStorage.removeItem("lms_pending_verify_qr_job");
        setQrModalOpen(false);
        runTicketGenerationVerify(nextJob);
      }
    },
    [qrJob, runTicketGenerationVerify],
  );

  useJobProgress(
    trackedQrJobId,
    qrJob?.lotteryPeriodId || null,
    handleQrJobProgress,
  );

  useEffect(() => {
    const rawPendingVerify = localStorage.getItem("lms_pending_verify_qr_job");
    if (!rawPendingVerify) return;

    try {
      const pendingVerify = JSON.parse(rawPendingVerify);
      if (!pendingVerify?.jobId || !pendingVerify?.lotteryPeriodId) return;
      if (verifiedQrJobRef.current === pendingVerify.jobId) return;

      verifiedQrJobRef.current = pendingVerify.jobId;
      runTicketGenerationVerify({
        jobId: pendingVerify.jobId,
        lotteryPeriodId: Number(pendingVerify.lotteryPeriodId),
        status: "completed",
        totalTickets: 0,
        processedTickets: 0,
        progressPercent: 100,
        errorMessage: null,
      }).finally(() => {
        localStorage.removeItem("lms_pending_verify_qr_job");
      });
    } catch {
      localStorage.removeItem("lms_pending_verify_qr_job");
    }
  }, [runTicketGenerationVerify]);

  useEffect(() => {
    if (!qrJob || String(qrJob.status || "").toLowerCase() !== "completed")
      return;
    if (verifiedQrJobRef.current === qrJob.jobId) return;

    verifiedQrJobRef.current = qrJob.jobId;
    setQrModalOpen(false);
    runTicketGenerationVerify(qrJob);
  }, [qrJob, runTicketGenerationVerify]);

  const handleSendToPrint = async (plan: IssuancePlan) => {
    setSubmitting(true);
    try {
      if (
        Number(plan.expectedPrintQuantity || 0) >= SYNC_PRINT_PACKAGE_ROW_LIMIT
      ) {
        const response = await createPrintPackageExportJob(plan.id);
        const payload = response?.data || response;
        if (payload?.jobId) {
          setStoredActivePrintExportJob(payload.jobId, plan.id);
          window.dispatchEvent(
            new CustomEvent(PRINT_EXPORT_JOB_STARTED_EVENT, {
              detail: { job: payload },
            }),
          );
        }
        toast.info(
          st('dang_tao_goi_in_o_nen_message'),
        );
        // Refresh ngay de canSendToPrint tra ve false, tranh nguoi dung bam "Gui in" nhieu lan
        // trong luc job export van dang chay nen.
        loadPlans();
        return;
      }

      const response = await createPrintPackageExportJob(plan.id);
      const payload = response?.data || response;
      if (payload?.jobId) {
        setStoredActivePrintExportJob(payload.jobId, plan.id);
        window.dispatchEvent(
          new CustomEvent(PRINT_EXPORT_JOB_STARTED_EVENT, {
            detail: { job: payload },
          }),
        );
      }
      toast.success(st('da_tao_goi_in_va_gui_message'));
      loadPlans();
    } catch (error: any) {
      toast.error(
        error?.message || st('khong_the_gui_nha_in_hoac_message'),
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Tra cứu 1 vé bất kỳ trong bộ vé vừa sinh của kỳ này
  const handleOpenSearchResult = (plan: IssuancePlan) => {
    setSearchPlan(plan);
    setSearchModalOpen(true);
  };

  // Xuất file ảnh QR (.png, gom theo thư mục ký hiệu, nén 1 file .zip) — luồng chạy song song, độc
  // lập với "Gửi in" — chạy nền qua job, tiến trình/tải file theo dõi ở GlobalJobProgress.
  const handleExportQrImages = async (plan: IssuancePlan) => {
    try {
      const response = await createQrImageExportJob(plan.id);
      const payload: any = response?.data || response;
      if (payload?.jobId) {
        setStoredActiveQrImageExportJob(payload.jobId, plan.id);
        window.dispatchEvent(
          new CustomEvent(QR_IMAGE_EXPORT_JOB_STARTED_EVENT, {
            detail: { job: payload },
          }),
        );
      }
      toast.info(
        st('dang_xuat_file_anh_qr_o_message'),
      );
    } catch (error: any) {
      toast.error(error?.message || st('khong_the_tao_job_xuat_file_message'));
    }
  };

  // Tập action-code tĩnh của cột "Thao tác" ở bảng danh sách kế hoạch — dùng để ẩn hẳn cột này
  // khi role hiện tại không có quyền với bất kỳ action nào trong cột (không phụ thuộc trạng thái dòng).
  const PLAN_ACTION_COLUMN_CODES = [
    "button-approve",
    "button-reject",
    "button-search-result",
    "button-send-to-print",
    "button-export-qr-images",
    "button-receive-warehouse",
    "button-resume-ticket-creation",
    "button-cancel-job",
    "button-history",
  ];
  const canShowPlanActionColumn = PageActionPermission.canDisplayAny(
    location.pathname,
    PLAN_ACTION_COLUMN_CODES,
  );

  const renderActionButtons = (plan: IssuancePlan) => {
    const status = Number(plan.status);
    // Ke hoach bi Huy dung luc dang/da sinh ma QR ("Huy tao ve") van con thao tac tiep theo
    // ("Duyet tao ve") nen khong tinh la khoa han nhu "Huy ke hoach" thong thuong.
    const canResumeTicketCreation =
      status === ISSUANCE_PLAN_STATUS.Cancel && !!plan.cancelledFromStatus;
    const isLocked =
      status === ISSUANCE_PLAN_STATUS.Accept ||
      (status === ISSUANCE_PLAN_STATUS.Cancel && !canResumeTicketCreation);

    // Ke hoach nay dang co job nen chay khong (sinh QR uu tien truoc, roi den xuat goi in, xuat
    // anh QR — cung thu tu uu tien voi PlanStatusColumn) — dung de hien nut "Huy" doc lap voi
    // trang thai ke hoach, vi job co the dang chay ngay ca khi ke hoach con o trang thai "Approved"
    // (truoc khi job sinh QR hoan tat) (xem REQUIRE_FIXBUGS.md muc 9.2).
    const activeQrJobId = activeQrGenJobIdByPlan.get(plan.id);
    const activePrintJobId = printingPlanIds.get(plan.id);
    const activeQrImageJobId = exportingQrImagePlanIds.get(plan.id);
    const activeJob = activeQrJobId
      ? {
          type: "qr" as const,
          jobId: activeQrJobId,
          label: st('huy_sinh_bo_so_qr_label'),
        }
      : activePrintJobId
        ? {
            type: "print" as const,
            jobId: activePrintJobId,
            label: st('huy_xuat_goi_in_label'),
          }
        : activeQrImageJobId
          ? {
              type: "qrImage" as const,
              jobId: activeQrImageJobId,
              label: st('huy_xuat_anh_qr_label'),
            }
          : null;

    return (
      <div className="d-flex justify-content-center gap-1 flex-nowrap">
        {status === ISSUANCE_PLAN_STATUS.Created && (
          <>
            {PageActionPermission.canDisplay(
              location.pathname,
              "button-approve",
            ) && (
              <Button
                color="success"
                outline
                size="sm"
                className="btn-icon"
                title={st('duyet_title')}
                data-action-code="button-approve"
                data-action-name="Duyệt kế hoạch"
                disabled={submitting}
                onClick={() =>
                  openConfirmAction({
                    title: st('xac_nhan_duyet_ke_hoach_title'),
                    message: st('ban_co_chac_muon_duyet_ke_message', { plan_code: plan.planCode }),
                    confirmText: st('duyet_ke_hoach_text'),
                    color: "success",
                    iconClass: "ri-checkbox-circle-line",
                    action: () => approveIssuancePlan(plan.id),
                    successMessage: st('da_phe_duyet_ke_hoach_text'),
                    // Sau khi duyet xong, dieu huong luon sang man Sinh bo so & Ma QR de nguoi
                    // dung tiep tuc thao tac ma khong phai tu chuyen man rieng.
                    onSuccess: () =>
                      navigate(
                        `/sinh-bo-so-qr?planCode=${encodeURIComponent(plan.planCode)}`,
                      ),
                  })
                }
              >
                <i className="ri-checkbox-circle-line"></i>
              </Button>
            )}
            {PageActionPermission.canDisplay(
              location.pathname,
              "button-reject",
            ) && (
              <Button
                color="danger"
                outline
                size="sm"
                className="btn-icon"
                title={st('huy_title')}
                data-action-code="button-reject"
                data-action-name="Hủy/Từ chối kế hoạch"
                disabled={submitting}
                onClick={() => setRejectPlan(plan)}
              >
                <i className="ri-close-circle-line"></i>
              </Button>
            )}
          </>
        )}

        {status === ISSUANCE_PLAN_STATUS.GenQr &&
          PageActionPermission.canDisplay(
            location.pathname,
            "button-search-result",
          ) && (
            <Button
              color="primary"
              outline
              size="sm"
              className="btn-icon"
              title={st('tra_cuu_ket_qua_ve_so_title')}
              data-action-code="button-search-result"
              data-action-name="Tra cứu kết quả vé số vừa tạo"
              onClick={() => handleOpenSearchResult(plan)}
            >
              <i className="ri-search-line"></i>
            </Button>
          )}

        {/* Gui in (download goi in Excel) va Xuat file QR anh — enable ca 2 trang thai Da sinh QR
            va Da gui nha in, de nguoi dung van tai lai duoc file du plan da chuyen trang thai. Voi
            SentPrint thi khong con gate theo canSendToPrint (co nghia "duoc phep gui") vi muc dich
            luc nay chi la tai lai, khong phai gui lai. */}
        {(status === ISSUANCE_PLAN_STATUS.GenQr ||
          status === ISSUANCE_PLAN_STATUS.SentPrint) && (
          <>
            {PageActionPermission.canDisplay(
              location.pathname,
              "button-send-to-print",
            ) && (
              <Button
                color="info"
                outline
                size="sm"
                className="btn-icon"
                title={
                  printingPlanIds.has(plan.id)
                    ? st('dang_xuat_goi_in_vui_long_text')
                    : st('gui_in_text')
                }
                data-action-code="button-send-to-print"
                data-action-name="Gửi in"
                disabled={
                  (status === ISSUANCE_PLAN_STATUS.GenQr &&
                    !plan.canSendToPrint) ||
                  submitting ||
                  printingPlanIds.has(plan.id)
                }
                onClick={() =>
                  openConfirmAction({
                    title: st('xac_nhan_gui_in_title'),
                    message: st('ban_co_chac_muon_chuyen_lenh_message', { plan_code: plan.planCode }),
                    confirmText: st('gui_in_text'),
                    color: "info",
                    iconClass: "ri-printer-line",
                    action: () => handleSendToPrint(plan),
                    successMessage: "",
                    handlesOwnFeedback: true,
                  })
                }
              >
                {printingPlanIds.has(plan.id) ? (
                  <i className="ri-loader-4-line"></i>
                ) : (
                  <i className="ri-printer-line"></i>
                )}
              </Button>
            )}
            {PageActionPermission.canDisplay(
              location.pathname,
              "button-export-qr-images",
            ) && (
              <Button
                color="secondary"
                outline
                size="sm"
                className="btn-icon"
                title={
                  exportingQrImagePlanIds.has(plan.id)
                    ? st('dang_xuat_anh_qr_vui_long_text')
                    : st('xuat_file_qr_anh_text')
                }
                data-action-code="button-export-qr-images"
                data-action-name="Xuất file QR ảnh"
                disabled={exportingQrImagePlanIds.has(plan.id)}
                onClick={() => handleExportQrImages(plan)}
              >
                {exportingQrImagePlanIds.has(plan.id) ? (
                  <i className="ri-loader-4-line"></i>
                ) : (
                  <i className="ri-qr-code-line"></i>
                )}
              </Button>
            )}
          </>
        )}

        {status === ISSUANCE_PLAN_STATUS.SentPrint && (
          <>
            {PageActionPermission.canDisplay(
              location.pathname,
              "button-print-house-delivery",
            ) && (
              <Button
                color="info"
                outline
                size="sm"
                className="btn-icon"
                title={st('ghi_nhan_giao_ve_cho_nha_title')}
                data-action-code="button-print-house-delivery"
                data-action-name="Ghi nhận giao vé cho nhà in"
                disabled={submitting}
                onClick={() => {
                  setPrintHouseDeliveryPlan(plan);
                  setPrintHouseDeliveryForm({
                    printHouseName: "",
                    address: "",
                    password: "",
                  });
                }}
              >
                <i className="ri-truck-line"></i>
              </Button>
            )}
            {PageActionPermission.canDisplay(
              location.pathname,
              "button-receive-warehouse",
            ) && (
              <Button
                color="warning"
                outline
                size="sm"
                className="btn-icon"
                title={st('tiep_nhan_title')}
                data-action-code="button-receive-warehouse"
                data-action-name="Tiếp nhận kho"
                disabled={submitting}
                onClick={() => setWarehousePlan(plan)}
              >
                <i className="ri-archive-line"></i>
              </Button>
            )}
            {PageActionPermission.canDisplay(
              location.pathname,
              "button-reject",
            ) && (
              <Button
                color="danger"
                outline
                size="sm"
                className="btn-icon"
                title={st('huy_title')}
                data-action-code="button-reject"
                data-action-name="Hủy/Từ chối kế hoạch"
                disabled={submitting}
                onClick={() => setRejectPlan(plan)}
              >
                <i className="ri-close-circle-line"></i>
              </Button>
            )}
          </>
        )}

        {canResumeTicketCreation &&
          PageActionPermission.canDisplay(
            location.pathname,
            "button-resume-ticket-creation",
          ) && (
            <Button
              color="success"
              outline
              size="sm"
              className="btn-icon"
              title={st('duyet_tao_ve_title')}
              data-action-code="button-resume-ticket-creation"
              data-action-name="Duyệt lại tạo vé"
              disabled={submitting}
              onClick={() =>
                openConfirmAction({
                  title: st('xac_nhan_duyet_tao_ve_title'),
                  message: st('ban_co_chac_muon_duyet_lai_message', { plan_code: plan.planCode }),
                  confirmText: st('duyet_tao_ve_title'),
                  color: "success",
                  iconClass: "ri-refresh-line",
                  action: () => resumeTicketCreationIssuancePlan(plan.id),
                  successMessage: st('da_duyet_lai_tao_ve_text'),
                })
              }
            >
              <i className="ri-refresh-line"></i>
            </Button>
          )}

        {isLocked && (
          <Button
            color="light"
            size="sm"
            className="btn-icon"
            title={st('da_khoa_title')}
            disabled
          >
            <i className="ri-lock-line text-muted"></i>
          </Button>
        )}

        {activeJob &&
          PageActionPermission.canDisplay(
            location.pathname,
            "button-cancel-job",
          ) && (
            <Button
              color="danger"
              outline
              size="sm"
              className="btn-icon"
              title={activeJob.label}
              data-action-code="button-cancel-job"
              data-action-name="Hủy tác vụ đang chạy (sinh QR/xuất gói in/xuất ảnh QR)"
              disabled={submitting}
              onClick={() =>
                setCancelJobTarget({
                  type: activeJob.type,
                  jobId: activeJob.jobId,
                  planCode: plan.planCode,
                })
              }
            >
              <i className="ri-close-circle-line"></i>
            </Button>
          )}

        {PageActionPermission.canDisplay(
          location.pathname,
          "button-history",
        ) && (
          <Button
            color="dark"
            outline
            size="sm"
            className="btn-icon"
            title={st('lich_su_ke_hoach_title')}
            data-action-code="button-history"
            data-action-name="Lịch sử kế hoạch"
            onClick={() => setHistoryPlan(plan)}
          >
            <i className="ri-history-line"></i>
          </Button>
        )}
      </div>
    );
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={st('ke_hoach_phat_hanh_text')}
            pageTitle={st('quan_ly_phat_hanh_text')}
          />

          <Card id="issuancePlanList">
            <CardHeader className="card-header border-0">
              <Row className="align-items-center gy-3 mb-5">
                <div className="col-sm">
                  <h5 className="card-title mb-0">
                    {st('danh_sach_ke_hoach_phat_hanh_title')}
                  </h5>
                </div>
                <div className="col-sm-auto">
                  <div className="d-flex gap-1 flex-wrap">
                    {PageActionPermission.canDisplay(
                      location.pathname,
                      "button-add",
                    ) && (
                      <button
                        type="button"
                        className="btn btn-success add-btn"
                        id="create-btn"
                        data-action-code="button-add"
                        data-action-name="Lập kế hoạch phát hành"
                        onClick={async () => {
                          setCreateOpen(true);
                          const templateList =
                            templates.length > 0
                              ? templates
                              : await loadTemplates();
                          // Reset toan bo form theo dung loai ve + so luong ve tao moi ky da cau hinh
                          // o System Configuration — Loai ve khong cho tu chon tay (xem Input "Loai ve"
                          // disabled ben duoi), luon lay theo configuredTicketTypeCode.
                          resetCreateForm(templateList);
                        }}
                      >
                        <i className="ri-add-line align-bottom me-1"></i>
                        {st('lap_ke_hoach_button')}
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn btn-soft-secondary"
                      disabled={loading}
                      onClick={() => {
                        setFilters(initialFilters);
                        setMonthInput(initialFilters.month);
                        setPagination((prev) => ({ ...prev, pageIndex: 1 }));
                      }}
                    >
                      <i className="ri-refresh-line align-bottom me-1"></i>
                      {st('xoa_loc_button')}
                    </button>
                  </div>
                </div>
              </Row>
            </CardHeader>
            <CardBody className="pt-0">
              <Row className="g-3 mb-3 align-items-end mb-5">
                <Col md={3}>
                  <Label className="form-label">{st('trang_thai_column')}</Label>
                  <Input
                    type="select"
                    className="form-select"
                    value={filters.status}
                    onChange={(event) => {
                      setFilters((prev) => ({
                        ...prev,
                        status: event.target.value,
                      }));
                      setPagination((prev) => ({ ...prev, pageIndex: 1 }));
                    }}
                  >
                    {statusOptions.map((status) => (
                      <option key={status.statusCode} value={status.statusCode}>
                        {status.statusName}
                      </option>
                    ))}
                  </Input>
                </Col>
                <Col md={3}>
                  <Label className="form-label">{st('thang_quay_label')}</Label>
                  <Input
                    type="text"
                    className="form-control"
                    placeholder={st('mm_yyyy_placeholder')}
                    value={monthInput}
                    onChange={(event) => {
                      setMonthInput(
                        formatDrawMonthSearchInput(event.target.value),
                      );
                    }}
                    onKeyDown={(event) => {
                      if (event.key !== "Enter") return;
                      event.preventDefault();
                      const value = monthInput.trim();
                      if (!value) {
                        setFilters((prev) => ({ ...prev, month: "" }));
                        setPagination((prev) => ({ ...prev, pageIndex: 1 }));
                        return;
                      }
                      if (!isValidDrawMonthSearch(value)) {
                        toast.warning(
                          st('vui_long_nhap_thang_quay_theo_message'),
                        );
                        return;
                      }
                      setFilters((prev) => ({ ...prev, month: value }));
                      setPagination((prev) => ({ ...prev, pageIndex: 1 }));
                    }}
                  />
                </Col>
                <Col md={6} className="text-md-end">
                  <span className="text-muted">
                    {st('tong_text_2')}{" "}
                    <span className="fw-semibold">
                      {formatNumber(pagination.totalItems)}
                    </span>{" "}
                    {st('ban_ghi_text')}
                  </span>
                </Col>
              </Row>

              <DataTableShell
                headerRow={
                  <tr>
                    <th className="text-center" style={{ width: 60 }}>
                      {st('stt_column')}
                    </th>
                    <th>{st('ma_kh_column')}</th>
                    <th>{st('loai_ve_mau_column')}</th>
                    <th>{st('ngay_quay_column')}</th>
                    <th>{st('so_luong_column')}</th>
                    <th>{st('dai_so_ky_hieu_column')}</th>
                    <th>{st('trang_thai_column')}</th>
                    {canShowPlanActionColumn && (
                      <th style={{ width: 140 }}>{st('thao_tac_column')}</th>
                    )}
                  </tr>
                }
              >
                {loading ? (
                  Array.from({ length: 5 }).map((_, row) => (
                    <tr key={row}>
                      {Array.from({
                        length: canShowPlanActionColumn ? 8 : 7,
                      }).map((__, col) => (
                        <td key={col}>
                          <div className="placeholder-glow">
                            <span className="placeholder col-8"></span>
                          </div>
                        </td>
                      ))}
                    </tr>
                  ))
                ) : plans.length === 0 ? (
                  <tr>
                    <td
                      colSpan={canShowPlanActionColumn ? 8 : 7}
                      className="text-center text-muted py-5"
                    >
                      <div className="mb-2">
                        <i className="ri-file-list-3-line fs-1 text-muted"></i>
                      </div>
                      {st('chua_co_ke_hoach_phat_hanh_text')}
                    </td>
                  </tr>
                ) : (
                  plans.map((plan, rowIndex) => (
                    <tr key={plan.id}>
                      <td className="text-center">
                        {getSttNumber(
                          pagination.pageIndex,
                          pagination.pageSize,
                          rowIndex,
                        )}
                      </td>
                      <td>
                        <div className="fw-medium text-primary">
                          {plan.planCode}
                        </div>
                        <small className="text-muted">
                          {formatDate(plan.createdAt)}
                        </small>
                      </td>
                      <td>
                        <div>{plan.ticketTypeName || plan.ticketType}</div>
                        <small className="text-muted">
                          {plan.ticketDesignTemplateName || "-"}
                        </small>
                      </td>
                      <td>{formatDate(plan.expectedDrawDate)}</td>
                      <td>{formatNumber(plan.expectedPrintQuantity)}</td>
                      <td>
                        <div
                          className="text-truncate"
                          style={{ maxWidth: 200, cursor: "pointer" }}
                          role="button"
                          title={st('xem_day_du_dai_so_ky_title')}
                          onClick={() => setRangeInfoPlan(plan)}
                        >
                          <div className="text-truncate">
                            {plan.numberRangeStart} - {plan.numberRangeEnd}
                          </div>
                          <small className="text-muted text-truncate d-block">
                            {plan.ticketSymbols?.join(", ") || "-"}
                          </small>
                        </div>
                      </td>
                      <td>
                        <PlanStatusColumn
                          planId={plan.id}
                          statusBadgeClassName={getSoftBadgeClass(
                            getStatusColor(plan.status, statuses),
                          )}
                          statusLabel={
                            plan.statusName ||
                            getStatusLabel(plan.status, statuses)
                          }
                          rejectionReason={plan.rejectionReason}
                        />
                        {plan.hasUnresolvedError && (
                          <span
                            className={`${getSoftBadgeClass("danger")} ms-1`}
                            role="button"
                            title={st('ke_hoach_con_loi_job_nen_title')}
                            data-action-code="button-plan-error"
                            data-action-name="Xem chi tiết kế hoạch lỗi"
                            onClick={() => setErrorPlan(plan)}
                          >
                            <i className="ri-error-warning-line align-bottom me-1"></i>
                            {st('co_loi_text')}
                          </span>
                        )}
                      </td>
                      {canShowPlanActionColumn && (
                        <td className="text-center">
                          {renderActionButtons(plan)}
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </DataTableShell>

              <PaginationBar
                currentPage={pagination.pageIndex}
                totalPages={pagination.totalPages}
                totalItems={pagination.totalItems}
                onPageChange={(page) =>
                  setPagination((prev) => ({ ...prev, pageIndex: page }))
                }
              />
            </CardBody>
          </Card>
        </Container>
      </div>

      <Modal
        id="showModal"
        isOpen={createOpen}
        toggle={() => !submitting && setCreateOpen(false)}
        centered
        size="lg"
      >
        <ModalHeader
          className="bg-light p-3"
          toggle={() => !submitting && setCreateOpen(false)}
        >
          {st('lap_ke_hoach_phat_hanh_title')}
        </ModalHeader>
        <Form onSubmit={handleCreate}>
          <ModalBody>
            {submitting && (
              <Alert color="info" className="d-flex align-items-center gap-2">
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                ></span>
                {st('dang_tao_ke_hoach_phat_hanh_message')}
              </Alert>
            )}
            {templates.length === 0 && (
              <Alert color="warning">
                {st('chua_co_mau_thiet_ke_ve_message')}
              </Alert>
            )}
            {!configuredTicketTypeCode && (
              <Alert color="warning">
                {st('chua_cau_hinh_loai_ve_mac_message')}{initialCreateForm.ticketType}
                {st('vui_long_vao_system_configuration_de_message')}
              </Alert>
            )}
            {configuredTicketTypeCode &&
              createForm.ticketType !== configuredTicketTypeCode && (
                <Alert color="danger">
                  {st('loai_ve_dang_chon_message')}{createForm.ticketType}{st('khong_khop_voi_cau_hinh_o_message')}{configuredTicketTypeCode}{st('vui_long_dong_va_mo_lai_message')}
                </Alert>
              )}
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>{st('loai_ve_label')}</Label>
                  {/* Loại vé luôn khoá theo System Configuration (không cho tự chọn — xem các Alert phía
                      trên), nên hiển thị dạng ô text disabled chỉ 1 giá trị, không dùng <select> để
                      tránh lộ danh sách loại vé khác trong DOM và mũi tên dropdown gây hiểu lầm là
                      chọn được. */}
                  <Input
                    type="text"
                    value={
                      ticketTypeOptions.find(
                        (option: { value: string; label: string }) =>
                          option.value === createForm.ticketType,
                      )?.label || createForm.ticketType
                    }
                    data-action-code="input-ticket-type"
                    data-action-name="Loại vé"
                    disabled
                    readOnly
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>
                    {st('mau_thiet_ke_ve_label')} <span className="text-danger">*</span>
                  </Label>
                  <div
                    data-action-code="input-ticket-design-template"
                    data-action-name="Mẫu thiết kế vé"
                  >
                    <AsyncSelect
                      isDisabled={
                        !PageActionPermission.canEnable(
                          location.pathname,
                          "input-ticket-design-template",
                        )
                      }
                      cacheOptions
                      defaultOptions={recentTemplateOptions}
                      loadOptions={loadTemplateOptions}
                      value={selectedTemplateOption}
                      inputValue={templateSearchInput}
                      onInputChange={handleTemplateInputChange}
                      onMenuOpen={handleTemplateMenuOpen}
                      placeholder={st('go_ma_hoac_ten_mau_ve_placeholder')}
                      noOptionsMessage={() => st('khong_tim_thay_mau_ve_phu_text')}
                      loadingMessage={() => st('dang_tim_text')}
                      onChange={(option) => {
                        setSelectedTemplateOption(
                          option as { value: number; label: string } | null,
                        );
                        // Xoa text tim kiem sau khi chon de AsyncSelect hien thi
                        // ngay gia tri vua chon (SingleValue) thay vi van giu text da go
                        setTemplateSearchInput("");
                        setCreateForm((prev) => ({
                          ...prev,
                          ticketDesignTemplateId:
                            (option as { value: number } | null)?.value || 0,
                        }));
                      }}
                    />
                  </div>
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>
                    {st('ngay_quay_du_kien_label')} <span className="text-danger">*</span>
                  </Label>
                  <div
                    data-action-code="input-expected-draw-date"
                    data-action-name="Ngày quay dự kiến"
                  >
                    <Flatpickr
                      className="form-control"
                      disabled={
                        !PageActionPermission.canEnable(
                          location.pathname,
                          "input-expected-draw-date",
                        )
                      }
                      required
                      placeholder={st('dd_mm_yyyy_placeholder')}
                      value={createForm.expectedDrawDate || undefined}
                      options={{
                        dateFormat: "Y-m-d",
                        altInput: true,
                        altFormat: "d/m/Y",
                        locale: Vietnamese,
                      }}
                      onChange={(_dates, dateStr) =>
                        setCreateForm((prev) => ({
                          ...prev,
                          expectedDrawDate: dateStr,
                        }))
                      }
                    />
                  </div>
                </FormGroup>
              </Col>
              {/* Ẩn số lượng vé — chỉ lấy từ System Configuration (quantityPerPeriod theo loại vé
                  đang cấu hình, xem resetCreateForm), không cho nhập tay */}
              <Col md={6}>
                <FormGroup>
                  <Label>{st('so_luong_ve_in_du_kien_label')}</Label>
                  <NumberInput
                    required
                    value={createForm.expectedPrintQuantity}
                    data-action-code="input-expected-print-quantity"
                    data-action-name="Số lượng vé in dự kiến"
                    onChange={() => {}}
                    readOnly
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={4} style={{ display: "none" }}>
                <FormGroup>
                  <Label>{st('dai_so_bat_dau_label')}</Label>
                  <Input
                    required
                    value={createForm.numberRangeStart}
                    onChange={(event) =>
                      setCreateForm((prev) => ({
                        ...prev,
                        numberRangeStart: event.target.value,
                      }))
                    }
                  />
                </FormGroup>
              </Col>
              <Col md={4} style={{ display: "none" }}>
                <FormGroup>
                  <Label>{st('dai_so_ket_thuc_label')}</Label>
                  <Input
                    required
                    value={createForm.numberRangeEnd}
                    onChange={(event) =>
                      setCreateForm((prev) => ({
                        ...prev,
                        numberRangeEnd: event.target.value,
                      }))
                    }
                  />
                </FormGroup>
              </Col>
              <Col md={12}>
                <FormGroup>
                  <Label>
                    {st('ky_hieu_ve_label')} <span className="text-danger">*</span>
                  </Label>
                  <Input
                    placeholder={st('a_b_c_placeholder')}
                    value={ticketSymbolsText}
                    data-action-code="input-ticket-symbols"
                    data-action-name="Ký hiệu vé"
                    disabled={
                      !PageActionPermission.canEnable(
                        location.pathname,
                        "input-ticket-symbols",
                      )
                    }
                    invalid={!!ticketSymbolsError}
                    onChange={(event) => {
                      setTicketSymbolsText(event.target.value);
                      setTicketSymbolsError(null);
                    }}
                  />
                  {ticketSymbolsError && (
                    <FormFeedback type="invalid">
                      {ticketSymbolsError}
                    </FormFeedback>
                  )}
                  <div className="d-flex flex-wrap gap-1 mt-2">
                    {ticketSymbolsText
                      .split(",")
                      .map((item) => item.trim().toUpperCase())
                      .filter(Boolean)
                      .map((symbol) => (
                        <Badge
                          color="light"
                          className="text-dark border"
                          key={symbol}
                        >
                          {symbol}
                        </Badge>
                      ))}
                  </div>
                </FormGroup>
              </Col>
            </Row>
          </ModalBody>
          <div className="modal-footer">
            <div className="hstack gap-2 justify-content-end">
              <Button
                color="light"
                onClick={() => setCreateOpen(false)}
                disabled={submitting}
              >
                {st('huy_title')}
              </Button>
              {PageActionPermission.canDisplay(
                location.pathname,
                "button-create-submit",
              ) && (
                <Button
                  color="success"
                  type="submit"
                  data-action-code="button-create-submit"
                  data-action-name="Tạo kế hoạch phát hành"
                  disabled={submitting || templates.length === 0}
                >
                  {submitting && (
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                  )}
                  {st('tao_ke_hoach_button')}
                </Button>
              )}
            </div>
          </div>
        </Form>
      </Modal>

      <ReasonPromptModal
        isOpen={!!rejectPlan}
        title={st('huy_ke_hoach_title')}
        label={st('ly_do_huy_label')}
        confirmLabel={st('huy_ke_hoach_title')}
        submitting={submitting}
        onCancel={() => setRejectPlan(null)}
        onSubmit={handleReject}
      />

      <ReasonPromptModal
        isOpen={!!cancelJobTarget}
        title={
          cancelJobTarget
            ? `${cancelJobTarget.type === "qr" ? st('huy_sinh_bo_so_qr_label') : cancelJobTarget.type === "print" ? st('huy_xuat_goi_in_label') : st('huy_xuat_anh_qr_label')} — ${cancelJobTarget.planCode}`
            : st('huy_tac_vu_text')
        }
        label={st('ly_do_huy_label')}
        confirmLabel={st('huy_tac_vu_text')}
        submitting={submitting}
        onCancel={() => setCancelJobTarget(null)}
        onSubmit={handleCancelActiveJob}
      />

      <HistoryTimelineModal
        isOpen={!!historyPlan}
        plan={historyPlan}
        onClose={() => setHistoryPlan(null)}
      />

      {/* Mục 13.5, REQUIRE_FIXBUGS.md — bấm badge "Có lỗi" mở đúng nội dung lịch sử lỗi + nút
          rerun sẵn có của ErrorPlanManagementTab, chỉ lọc theo đúng kế hoạch vừa bấm. */}
      <Modal
        isOpen={!!errorPlan}
        toggle={() => setErrorPlan(null)}
        centered
        size="xl"
      >
        <ModalHeader className="bg-light p-3" toggle={() => setErrorPlan(null)}>
          {st('chi_tiet_loi_title')} {errorPlan?.planCode}
        </ModalHeader>
        <ModalBody>
          {errorPlan && (
            <ErrorPlanManagementTab
              plans={[errorPlan]}
              initialPlanId={errorPlan.id}
            />
          )}
        </ModalBody>
      </Modal>

      <Modal
        isOpen={!!printHouseDeliveryPlan}
        toggle={() => setPrintHouseDeliveryPlan(null)}
        centered
      >
        <ModalHeader
          className="bg-light p-3"
          toggle={() => setPrintHouseDeliveryPlan(null)}
        >
          {st('ghi_nhan_giao_ve_cho_nha_title')}
        </ModalHeader>
        <Form onSubmit={handleCreatePrintHouseDelivery}>
          <ModalBody>
            <FormGroup>
              <Label>
                {st('ten_nha_in_label')} <span className="text-danger">*</span>
              </Label>
              <Input
                type="text"
                required
                autoFocus
                value={printHouseDeliveryForm.printHouseName}
                data-action-code="input-print-house-name"
                data-action-name="Tên nhà in"
                onChange={(event) =>
                  setPrintHouseDeliveryForm((prev) => ({
                    ...prev,
                    printHouseName: event.target.value,
                  }))
                }
              />
            </FormGroup>
            <FormGroup>
              <Label>
                {st('dia_chi_label')} <span className="text-danger">*</span>
              </Label>
              <Input
                type="text"
                required
                value={printHouseDeliveryForm.address}
                data-action-code="input-print-house-address"
                data-action-name="Địa chỉ nhà in"
                onChange={(event) =>
                  setPrintHouseDeliveryForm((prev) => ({
                    ...prev,
                    address: event.target.value,
                  }))
                }
              />
            </FormGroup>
            <FormGroup className="mb-0">
              <Label>
                {st('mat_khau_bao_ve_file_zip_label')} <span className="text-danger">*</span>
              </Label>
              <Input
                type="text"
                required
                value={printHouseDeliveryForm.password}
                data-action-code="input-print-house-password"
                data-action-name="Mật khẩu bảo vệ file ZIP"
                onChange={(event) =>
                  setPrintHouseDeliveryForm((prev) => ({
                    ...prev,
                    password: event.target.value,
                  }))
                }
              />
            </FormGroup>
          </ModalBody>
          <div className="modal-footer">
            <div className="hstack gap-2 justify-content-end">
              <Button
                color="light"
                onClick={() => setPrintHouseDeliveryPlan(null)}
                disabled={submitting}
              >
                {st('huy_title')}
              </Button>
              <Button color="info" type="submit" disabled={submitting}>
                {submitting && (
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                )}
                {st('ghi_nhan_button')}
              </Button>
            </div>
          </div>
        </Form>
      </Modal>

      <ReceiveWarehouseStepModal
        isOpen={!!warehousePlan}
        plan={warehousePlan}
        submitting={submitting}
        onSubmit={handleReceiveWarehouse}
        onClose={() => setWarehousePlan(null)}
      />

      <Modal
        isOpen={!!confirmAction}
        toggle={() => !submitting && setConfirmAction(null)}
        centered
      >
        <ModalHeader
          className="bg-light p-3"
          toggle={() => !submitting && setConfirmAction(null)}
        >
          {confirmAction?.title}
        </ModalHeader>
        <ModalBody className="text-center p-4">
          <div
            className={`avatar-lg mx-auto mb-3 rounded-circle bg-${confirmAction?.color || "primary"}-subtle d-flex align-items-center justify-content-center`}
          >
            <i
              className={`${confirmAction?.iconClass || "ri-question-line"} fs-1 text-${confirmAction?.color || "primary"}`}
            ></i>
          </div>
          <p className="text-muted mb-0">{confirmAction?.message}</p>
        </ModalBody>
        <div className="modal-footer">
          <div className="hstack gap-2 justify-content-end">
            <Button
              color="light"
              onClick={() => setConfirmAction(null)}
              disabled={submitting}
            >
              {st('dong_button')}
            </Button>
            {PageActionPermission.canDisplay(
              location.pathname,
              "button-confirm-action",
            ) && (
              <Button
                color={confirmAction?.color || "primary"}
                data-action-code="button-confirm-action"
                data-action-name="Xác nhận thao tác"
                onClick={handleConfirmAction}
                disabled={submitting}
              >
                {submitting && (
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                )}
                {confirmAction?.confirmText}
              </Button>
            )}
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={verifyModalOpen}
        toggle={() => !verifyLoading && setVerifyModalOpen(false)}
        centered
        size="lg"
      >
        <ModalHeader
          className="bg-light p-3"
          toggle={() => !verifyLoading && setVerifyModalOpen(false)}
        >
          {st('kiem_tra_ve_so_sau_khi_title')}
        </ModalHeader>
        <ModalBody>
          {verifyLoading && (
            <Alert color="info" className="d-flex align-items-center gap-2">
              <span
                className="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              ></span>
              {st('dang_kiem_tra_lai_ve_so_message')}
            </Alert>
          )}

          {!verifyLoading && verifyResult && (
            <>
              <Row className="g-3 mb-3">
                <Col md={4}>
                  <div className="text-muted small">{st('ky_column')}</div>
                  <div className="fw-semibold">
                    {verifyResult.lotteryPeriodId}
                  </div>
                </Col>
                <Col md={4}>
                  <div className="text-muted small">{st('ma_ky_text')}</div>
                  <div className="fw-semibold">{verifyResult.planCode}</div>
                </Col>
                <Col md={4}>
                  <div className="text-muted small">{st('ngay_tra_thuong_text')}</div>
                  <div className="fw-semibold">
                    {formatDate(verifyResult.expectedDrawDate)}
                  </div>
                </Col>
              </Row>

              <Alert
                color={verifyResult.isValid ? "success" : "warning"}
                className="mb-3"
              >
                {verifyResult.isValid
                  ? st('kiem_tra_thanh_cong_du_lieu_text')
                  : st('kiem_tra_phat_hien_du_lieu_text')}
              </Alert>

              <Row className="g-3">
                {[
                  {
                    label: st('tong_ve_duoc_tao_label'),
                    value: verifyResult.generatedTotalTickets,
                    color: "primary",
                  },
                  {
                    label: st('ve_tao_loi_label'),
                    value: verifyResult.brokenTickets,
                    color:
                      verifyResult.brokenTickets > 0 ? "danger" : "success",
                  },
                  {
                    label: st('ve_tao_trung_label'),
                    value: verifyResult.duplicateTickets,
                    color:
                      verifyResult.duplicateTickets > 0 ? "danger" : "success",
                  },
                  {
                    label: st('ve_tao_thanh_cong_label'),
                    value: verifyResult.successfulTickets,
                    color: verifyResult.isValid ? "success" : "warning",
                  },
                ].map((item) => (
                  <Col md={6} key={item.label}>
                    <div className="border rounded p-3 h-100">
                      <div className="text-muted large">{item.label}</div>
                      <div className={`fs-5 fw-semibold text-${item.color}`}>
                        {formatNumber(item.value)} /{" "}
                        {formatNumber(verifyResult.expectedTotalTickets)}
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>
            </>
          )}
        </ModalBody>
        <div className="modal-footer">
          <Button
            color="primary"
            disabled={verifyLoading}
            onClick={() => setVerifyModalOpen(false)}
          >
            {st('dong_button')}
          </Button>
        </div>
      </Modal>

      <SearchByPrizeModal
        isOpen={searchModalOpen}
        onClose={() => setSearchModalOpen(false)}
        lotteryPeriodId={searchPlan?.id}
        planCode={searchPlan?.planCode}
        expectedDrawDate={searchPlan?.expectedDrawDate}
        defaultNumberFrom={searchPlan?.numberRangeStart}
        defaultNumberTo={searchPlan?.numberRangeEnd}
        ticketSymbols={searchPlan?.ticketSymbols}
      />

      <Modal
        isOpen={!!rangeInfoPlan}
        toggle={() => setRangeInfoPlan(null)}
        centered
      >
        <ModalHeader
          className="bg-light p-3"
          toggle={() => setRangeInfoPlan(null)}
        >
          {st('dai_so_ky_hieu_title')} {rangeInfoPlan?.planCode}
        </ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <Label className="form-label text-muted mb-1">{st('dai_so_column')}</Label>
            <div className="fw-semibold">
              {rangeInfoPlan?.numberRangeStart} -{" "}
              {rangeInfoPlan?.numberRangeEnd}
            </div>
          </div>
          <div>
            <Label className="form-label text-muted mb-1">
              {st('ky_hieu_label')}{rangeInfoPlan?.ticketSymbols?.length || 0})
            </Label>
            <div className="d-flex flex-wrap gap-1">
              {(rangeInfoPlan?.ticketSymbols || []).map((s) => (
                <Badge color="light" className="text-dark border" key={s}>
                  {s}
                </Badge>
              ))}
            </div>
          </div>
        </ModalBody>
        <div className="modal-footer">
          <Button color="light" onClick={() => setRangeInfoPlan(null)}>
            {st('dong_button')}
          </Button>
        </div>
      </Modal>

      {qrModalOpen && qrJob && qrPanelCollapsed && (
        <div
          className="position-fixed"
          style={{ top: 88, right: 24, zIndex: 1060 }}
        >
          <button
            type="button"
            className="btn btn-light border shadow-lg d-flex align-items-center gap-2 rounded-pill py-2 px-3"
            title={st('phong_to_tien_trinh_sinh_bo_title')}
            onClick={() => setQrPanelCollapsed(false)}
          >
            <i
              className={`${
                qrJob.status === "completed"
                  ? "ri-check-double-line text-success"
                  : qrJob.status === "failed"
                    ? "ri-close-circle-line text-danger"
                    : qrJob.status === "stalled"
                      ? "ri-alarm-warning-line text-warning"
                      : "ri-loader-4-line text-primary"
              } fs-5`}
            ></i>
            <span className="fw-semibold small">
              {Number(qrJob.progressPercent || 0).toFixed(0)}%
            </span>
          </button>
        </div>
      )}

      {qrModalOpen && qrJob && !qrPanelCollapsed && (
        <div
          className="position-fixed"
          style={{
            top: 88,
            right: 24,
            zIndex: 1060,
            width: "min(420px, calc(100vw - 32px))",
          }}
        >
          <Alert color="light" className="border shadow-lg mb-0" fade={false}>
            <div className="d-flex align-items-start gap-3">
              <i
                className={`${
                  qrJob.status === "completed"
                    ? "ri-check-double-line text-success"
                    : qrJob.status === "failed"
                      ? "ri-close-circle-line text-danger"
                      : qrJob.status === "stalled"
                        ? "ri-alarm-warning-line text-warning"
                        : "ri-loader-4-line text-primary"
                } fs-3`}
              ></i>
              <div className="flex-grow-1 min-w-0">
                <div className="d-flex align-items-start justify-content-between gap-2">
                  <div>
                    <div className="fw-semibold">
                      {st('tien_trinh_sinh_bo_so_qr_text')}
                    </div>
                    <div className="text-muted small text-truncate">
                      {st('ky_text')} {qrJob.lotteryPeriodId}
                    </div>
                  </div>
                  <div className="d-flex align-items-center gap-1">
                    <button
                      type="button"
                      className="btn btn-sm btn-light p-1 lh-1"
                      aria-label={st('thu_nho_label')}
                      title={st('thu_nho_label')}
                      onClick={() => setQrPanelCollapsed(true)}
                    >
                      <i className="ri-subtract-line"></i>
                    </button>
                    {(qrJob.status === "completed" ||
                      qrJob.status === "failed" ||
                      qrJob.status === "stalled") && (
                      <Button
                        close
                        type="button"
                        aria-label={st('dong_button')}
                        onClick={() => setQrModalOpen(false)}
                      />
                    )}
                  </div>
                </div>

                <div className="mt-3">
                  <div className="d-flex justify-content-between small mb-1">
                    <span className="fw-semibold">{qrJob.status}</span>
                    <span className="fw-semibold">
                      {Number(qrJob.progressPercent || 0).toFixed(2)}%
                    </span>
                  </div>
                  <Progress
                    value={Math.max(
                      0,
                      Math.min(100, qrJob.progressPercent || 0),
                    )}
                    color={
                      qrJob.status === "failed"
                        ? "danger"
                        : qrJob.status === "stalled"
                          ? "warning"
                          : qrJob.status === "completed"
                            ? "success"
                            : "primary"
                    }
                    style={{ height: "10px" }}
                    animated={
                      qrJob.status !== "completed" &&
                      qrJob.status !== "failed" &&
                      qrJob.status !== "stalled"
                    }
                  />
                </div>

                <div className="d-flex justify-content-between text-muted small mt-2">
                  <span>{st('da_xu_ly_text_2')}</span>
                  <span className="fw-semibold">
                    {formatNumber(qrJob.processedTickets)} /{" "}
                    {formatNumber(qrJob.totalTickets)}
                  </span>
                </div>

                {(qrJob.status === "failed" || qrJob.status === "stalled") && (
                  <>
                    <div
                      className={`small mt-2 text-break ${qrJob.status === "stalled" ? "text-warning" : "text-danger"}`}
                    >
                      {qrJob.errorMessage || st('sinh_bo_so_qr_that_bai_text')}
                    </div>
                    <div className="text-muted small mt-2 fst-italic">
                      {st('bam_sinh_bo_so_qr_de_text')}
                    </div>
                    <JobErrorLogList
                      fetchLogs={() =>
                        getQrJobErrorLogsApi(qrJob.lotteryPeriodId)
                      }
                    />
                  </>
                )}
              </div>
            </div>
          </Alert>
        </div>
      )}

      {exportModalOpen && exportJob && exportPanelCollapsed && (
        <div
          className="position-fixed"
          style={{ top: 88, right: 24, zIndex: 1060 }}
        >
          <button
            type="button"
            className="btn btn-light border shadow-lg d-flex align-items-center gap-2 rounded-pill py-2 px-3"
            title={st('phong_to_tien_trinh_export_goi_title')}
            onClick={() => setExportPanelCollapsed(false)}
          >
            {exportDownloading ? (
              <span
                className="spinner-border spinner-border-sm text-info"
                role="status"
                aria-hidden="true"
              ></span>
            ) : (
              <i
                className={`${
                  exportJob.status === "completed"
                    ? "ri-check-double-line text-success"
                    : exportJob.status === "failed"
                      ? "ri-close-circle-line text-danger"
                      : exportJob.status === "stalled"
                        ? "ri-alarm-warning-line text-warning"
                        : "ri-loader-4-line text-primary"
                } fs-5`}
              ></i>
            )}
            <span className="fw-semibold small">
              {exportDownloading
                ? st('dang_tai_text')
                : `${Number(exportJob.progressPercent || 0).toFixed(0)}%`}
            </span>
          </button>
        </div>
      )}

      {exportModalOpen && exportJob && !exportPanelCollapsed && (
        <div
          className="position-fixed"
          style={{
            top: 88,
            right: 24,
            zIndex: 1060,
            width: "min(420px, calc(100vw - 32px))",
          }}
        >
          <Alert color="light" className="border shadow-lg mb-0" fade={false}>
            <div className="d-flex align-items-start gap-3">
              {exportDownloading ? (
                <span
                  className="spinner-border spinner-border-sm text-info mt-1"
                  role="status"
                  aria-hidden="true"
                ></span>
              ) : (
                <i
                  className={`${
                    exportJob.status === "completed"
                      ? "ri-check-double-line text-success"
                      : exportJob.status === "failed"
                        ? "ri-close-circle-line text-danger"
                        : exportJob.status === "stalled"
                          ? "ri-alarm-warning-line text-warning"
                          : "ri-loader-4-line text-primary"
                  } fs-3`}
                ></i>
              )}
              <div className="flex-grow-1 min-w-0">
                <div className="d-flex align-items-start justify-content-between gap-2">
                  <div>
                    <div className="fw-semibold">{st('tien_trinh_export_goi_in_text')}</div>
                    <div className="text-muted small text-truncate">
                      {st('job_text')} {exportJob.jobId}
                    </div>
                  </div>
                  <div className="d-flex align-items-center gap-1">
                    <button
                      type="button"
                      className="btn btn-sm btn-light p-1 lh-1"
                      aria-label={st('thu_nho_label')}
                      title={st('thu_nho_label')}
                      onClick={() => setExportPanelCollapsed(true)}
                    >
                      <i className="ri-subtract-line"></i>
                    </button>
                    {(exportJob.status === "completed" ||
                      exportJob.status === "failed" ||
                      exportJob.status === "stalled") && (
                      <Button
                        close
                        type="button"
                        aria-label={st('dong_button')}
                        onClick={() => setExportModalOpen(false)}
                      />
                    )}
                  </div>
                </div>

                <div className="mt-3">
                  <div className="d-flex justify-content-between small mb-1">
                    <span className="fw-semibold">{exportJob.status}</span>
                    <span className="fw-semibold">
                      {Number(exportJob.progressPercent || 0).toFixed(2)}%
                    </span>
                  </div>
                  <Progress
                    value={Math.max(
                      0,
                      Math.min(100, exportJob.progressPercent || 0),
                    )}
                    color={
                      exportJob.status === "failed"
                        ? "danger"
                        : exportJob.status === "stalled"
                          ? "warning"
                          : exportJob.status === "completed"
                            ? "success"
                            : "primary"
                    }
                    style={{ height: "10px" }}
                    animated={
                      exportJob.status !== "completed" &&
                      exportJob.status !== "failed" &&
                      exportJob.status !== "stalled"
                    }
                  />
                </div>

                <div className="d-flex justify-content-between text-muted small mt-2">
                  <span>{st('da_xu_ly_text_2')}</span>
                  <span className="fw-semibold">
                    {formatNumber(exportJob.processedRows)} /{" "}
                    {formatNumber(exportJob.totalRows)}
                  </span>
                </div>

                {exportJob.status === "completed" && exportDownloading && (
                  <div className="mt-3">
                    <div className="d-flex justify-content-between small mb-1">
                      <span className="fw-semibold text-info">
                        {st('dang_tai_xuong_text')}
                      </span>
                    </div>
                    <Progress
                      animated
                      striped
                      color="info"
                      value={100}
                      style={{ height: "10px" }}
                    />
                    <div className="text-muted small mt-2">
                      {st('dang_nen_va_tai_file_goi_text')}
                    </div>
                  </div>
                )}

                {exportJob.status === "completed" && !exportDownloading && (
                  <div className="text-success small mt-2">
                    {st('goi_in_da_san_sang_he_text')}
                  </div>
                )}

                {(exportJob.status === "failed" ||
                  exportJob.status === "stalled") && (
                  <>
                    <div
                      className={`small mt-2 text-break ${exportJob.status === "stalled" ? "text-warning" : "text-danger"}`}
                    >
                      {exportJob.errorMessage || st('export_goi_in_that_bai_text')}
                    </div>
                    <div className="text-muted small mt-2 fst-italic">
                      {st('bam_gui_in_de_tao_lai_text')}
                    </div>
                    <JobErrorLogList
                      fetchLogs={() =>
                        getPrintPackageJobErrorLogsApi(exportJob.issuancePlanId)
                      }
                    />
                  </>
                )}
              </div>
            </div>
          </Alert>
        </div>
      )}
    </React.Fragment>
  );
};

export default IssuancePlanPage;
